import { ArtistImage } from "models/ArtistImage";
import { WorkflowListDto } from "models/workflows/WorkflowDto";
import { useEffect, useState } from "react";
import { ApiService } from "services/ApiService";
import ArtistImageComponent from "./ArtistImageComponent";
import ArtistImages from "./ArtistImages";
import { useNavigate } from "react-router-dom";


export interface ArtistHeroSectionProps {
    artistId: string;
    isVisitor: boolean;
};

const ArtistHeroSection: React.FC<ArtistHeroSectionProps> = ({ artistId, isVisitor }) => {

    const [headline, setHeadline] = useState<string>("");
    const [subHeadline, setSubHeadline] = useState<string>("");
    const [artistImage, setArtistImage] = useState<ArtistImage>();
    const [bookingLinks, setBookingLinks] = useState<WorkflowListDto[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editSnapshot, setEditSnapshot] = useState<{
        headline: string;
        subHeadline: string;
        artistImage?: ArtistImage;
    } | null>(null);
    const [createNewHeroSection, setCreateNewHeroSection] = useState<boolean>(false);
    const [showImageSelector, setShowImageSelector] = useState<boolean>(false); // New state for toggling image selector
    const [showNoPhotosModal, setShowNoPhotosModal] = useState<boolean>(false); // State for no photos modal
    const [availableImages, setAvailableImages] = useState<ArtistImage[]>([]); // Store available images
    const navigate = useNavigate();
    const env = process.env.REACT_APP_API_BASE_URL;

    useEffect(() => {
        const fetchHeroSection = async () => {
            try {
                const response = await ApiService.getArtistHeroSection(artistId);
                if (response.success && response.data) {

                    let section = response.data;
                    setHeadline(section.headline);
                    setSubHeadline(section.subheadline);
                    setArtistImage(section.backgroundImageUrl);
                    // Ensure each booking link has a full workflow URL (env + artists + artistId + workflows + workflowId)
                    const base = env ?? "";
                    const normalized = (section.bookingLinks || []).map((link: WorkflowListDto) => ({
                        ...link,
                        workflowLink: link.workflowLink ?? `${base}artists/${artistId}/teams/${link.teamId}/workflows/${link.id}`
                    }));
                    setBookingLinks(normalized);

                    if (section.headline.length === 0) {
                        setCreateNewHeroSection(true);
                    }
                }
            } catch (error) {
                console.error("Error fetching workflows:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchHeroSection();
    }, [artistId]);

    useEffect(() => {
        const fetchImages = async () => {
            const response = await ApiService.getArtistImages(artistId);
            if (response.success && response.data) {
                setAvailableImages(response.data);
            }
        };
        fetchImages();
    }, [artistId, showImageSelector]);

    if (isLoading) return <h1>Loading . . . </h1>

    const handleImageSelect = (image: ArtistImage) => {
        setArtistImage(image); // Set the selected image
        setShowImageSelector(false); // Hide the image selector
    };

    const handleSelectPhotoClick = () => {
        if (availableImages.length === 0) {
            setShowNoPhotosModal(true); // Show modal if no photos
        } else {
            setShowImageSelector(true); // Show image selector
        }
    };

    const handleEditClick = () => {
        setEditSnapshot({ headline, subHeadline, artistImage });
        setShowImageSelector(false);
        setIsEditing(() => true);
    };

    const handleCancelEdit = () => {
        if (editSnapshot) {
            setHeadline(editSnapshot.headline);
            setSubHeadline(editSnapshot.subHeadline);
            setArtistImage(editSnapshot.artistImage);
        }
        setShowImageSelector(false);
        setIsEditing(false);
    };

    const handleSaveArtistHeroSection = async () => {

        try {
            const simplifiedSection = {
                headline: headline,
                subheadline: subHeadline,
                backgroundImageUrlId: artistImage?.id || "",
                bookingLinks: bookingLinks,
            };

            if (createNewHeroSection) {
                const response = await ApiService.saveArtistHeroSectionAsync(artistId, simplifiedSection);
                if (response.success && response.data) {

                    let section = response.data;
                    setHeadline(section.headline);
                    setSubHeadline(section.subheadline);
                    setArtistImage(section.backgroundImageUrl);
                        // Normalize booking links so they have workflowLink set
                        const base = env ?? "";
                        const normalized = (section.bookingLinks || []).map((link: WorkflowListDto) => ({
                            ...link,
                            workflowLink: link.workflowLink ?? `${base}artists/${artistId}/workflows/${link.id}`
                        }));
                        //setBookingLinks(normalized);
                }
            }
            else {
                const response = await ApiService.updateArtistHeroSectionAsync(artistId, simplifiedSection);
                
                if (response.success && response.data) {

                    let section = response.data;
                    setHeadline(section.headline);
                    setSubHeadline(section.subheadline);
                    setArtistImage(section.backgroundImageUrl);
                    // Normalize booking links so they have workflowLink set
                    const base = env ?? "";
                    const normalized = (section.bookingLinks || []).map((link: WorkflowListDto) => ({
                        ...link,
                        workflowLink: link.workflowLink ?? `${base}artists/${artistId}/teams/${link.teamId}/workflows/${link.id}/projects`
                    }));
                    //setBookingLinks(normalized);
                }
            }
        }
        catch (error) {
            console.error("Error saving artist hero section:", error);
        }
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <div className="max-w-2xl mx-auto p-6 bg-white shadow-md rounded-xl space-y-6">
                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="bg-gray-100 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-200 transition duration-200"
                    >
                        Exit & Cancel
                    </button>
                </div>
                <div>
                    <label className="block text-lg font-semibold text-gray-700 mb-1">Headline</label>
                    <input
                        type="text"
                        value={headline}
                        onChange={(e) => setHeadline(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <h2 className="text-2xl font-bold mt-4">{headline}</h2>
                </div>

                <div>
                    <label className="block text-lg font-semibold text-gray-700 mb-1">Subheadline</label>
                    <input
                        type="text"
                        value={subHeadline}
                        onChange={(e) => setSubHeadline(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <h3 className="text-xl text-gray-600 mt-2">{subHeadline}</h3>
                </div>

                {!isVisitor && (
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={handleSaveArtistHeroSection}
                            className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 transition duration-200"
                        >
                            Save Section
                        </button>
                        <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="bg-gray-100 text-gray-800 px-6 py-2 rounded-md hover:bg-gray-200 transition duration-200"
                        >
                            Cancel
                        </button>
                    </div>
                )}

                {artistImage && (
                    <div className="mt-4">
                        <ArtistImageComponent
                            artistId={artistId}
                            artistImage={artistImage}
                            isVisitor={isVisitor}
                        />
                    </div>
                )}

                {!isVisitor && (
                    <button
                        onClick={handleSelectPhotoClick} // Check for photos before showing selector
                        className="mt-4 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition duration-200"
                    >
                        Select Photo
                    </button>
                )}

                {showImageSelector && (
                    <div className="mt-4">
                        <ArtistImages
                            artistId={artistId}
                            isVisitor={isVisitor}
                            refreshKey={0} // Pass a refresh key if needed
                            onClick={(image) => handleImageSelect(image)} // Pass the image selection handler
                        />
                    </div>
                )}

                {/* No Photos Modal */}
                {showNoPhotosModal && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                        <div className="bg-white p-6 rounded-lg shadow-lg max-w-md relative">
                            <button
                                onClick={() => setShowNoPhotosModal(false)}
                                className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 text-2xl"
                            >
                                &times;
                            </button>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">No Photos Available</h3>
                            <p className="text-gray-700 mb-4">
                                Please click on the Portfolio section and add your photos first. Once you've uploaded some photos, come back here to select a photo for your landing page.
                            </p>
                            <button
                                onClick={() => setShowNoPhotosModal(false)}
                                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition duration-200"
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="max-w-3xl mx-auto bg-white p-8 rounded-xl shadow-md space-y-6">
            <div className="flex justify-between items-start">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900">{headline}</h2>
                    <h3 className="text-xl text-gray-600 mt-1">{subHeadline}</h3>
                </div>
                {!isVisitor && (
                    <button
                        onClick={handleEditClick}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition duration-200 text-sm"
                    >
                        Edit Artist Page
                    </button>
                )}
            </div>

            <div>
                {/* {bookingLinks && bookingLinks.length > 0 &&} */}
                <p className="text-md font-medium text-gray-800 mb-2">Book a custom appointment here:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                    {bookingLinks &&
                        bookingLinks.map((link) => (
                            <li key={link.id} className="pl-1">
                                <button
                                    onClick={() => navigate(`/artist/${artistId}/teams/${link.teamId}/workflows/${link.id}/projects`)}
                                    className="text-indigo-600 hover:underline"
                                > 
                                    {link.workflowLink}
                                </button>
                            </li>
                        ))}
                </ul>
            </div>

            {artistImage && (
                <div className="mt-4 flex items-center justify-center">
                    <ArtistImageComponent
                        artistId={artistId}
                        artistImage={artistImage}
                        isVisitor={isVisitor}
                        isEditable={false}
                    />
                </div>
            )}


        </div>

    );

};

export default ArtistHeroSection;